import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { PaginatedCustomerListQueryFilters } from '@/api/apis/customer';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '@/queries/utils';
import { SortOrderQuery, UseQueryConfig } from '@/api/types';
import { BASE_QUERY_KEYS } from '@/queries/consts';
import { getCustomerListWithTXCenter, getCustomerByIdWithTXCenter } from './utils';
import { customerQueryKeys } from './customer.keys';

export const customerQueries = {
  list: (listQuery: PaginatedCustomerListQueryFilters) =>
    queryOptions({
      queryKey: customerQueryKeys.getList(listQuery),
      queryFn: () => getCustomerListWithTXCenter(listQuery),
      placeholderData: keepPreviousData,
    }),
  items: (listQuery: PaginatedCustomerListQueryFilters) =>
    queryOptions({
      queryKey: customerQueryKeys.getList(listQuery),
      queryFn: () => api.customer.getCustomerList(listQuery),
      placeholderData: keepPreviousData,
    }),
  item: (customerId: string) =>
    queryOptions({
      queryKey: customerQueryKeys.getItem(customerId),
      queryFn: () => api.customer.getCustomerById(customerId),
    }),
  composedItem: (customerId?: string) =>
    queryOptions({
      queryKey: customerQueryKeys.getItem(customerId, BASE_QUERY_KEYS.composed),
      queryFn: () => getCustomerByIdWithTXCenter({ customerId }),
    }),
  getByUserId: (userId: string) =>
    queryOptions({
      queryKey: customerQueryKeys.getItem(userId, BASE_QUERY_KEYS.composed),
      queryFn: () => getCustomerByIdWithTXCenter({ userId }),
    }),
  getByUserIdSimple: (userId: string) =>
    queryOptions({
      queryKey: customerQueryKeys.getItem(userId, BASE_QUERY_KEYS.composed),
      queryFn: () => api.customer.getCustomerByUserId(userId),
    }),
  infiniteList: (searchPagination: PaginatedCustomerListQueryFilters) =>
    infiniteQueryOptions({
      queryKey: customerQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) => api.customer.getCustomerList({ ...searchPagination, pageIndex }),
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
};

export const useCustomerListQuery = (listQuery: PaginatedCustomerListQueryFilters) => {
  return useQuery(customerQueries.list(listQuery));
};

export const useCustomerItemsQuery = (listQuery: PaginatedCustomerListQueryFilters, options?: UseQueryConfig) => {
  return useQuery({
    ...customerQueries.items(listQuery),
    ...options,
  });
};

export const useComposedCustomerByIdQuery = (id?: string) => {
  return useQuery(customerQueries.composedItem(id));
};

export const useInfiniteCustomerListQuery = (filters?: PaginatedCustomerListQueryFilters & SortOrderQuery) => {
  return useInfiniteQuery(
    customerQueries.infiniteList({
      itemsPerPage: filters?.itemsPerPage || 10,
      pageIndex: 0,
      searchQuery: filters?.searchQuery,
      roles: filters?.roles,
      txCenters: filters?.txCenters,
      priorityCustomerIds: filters?.priorityCustomerIds,
      enabled: filters?.enabled,
      orderBy: filters?.orderBy,
      asc: filters?.asc,
    }),
  );
};
